import {graphqlQuery} from '../utils/graphqlQuery';
import {log} from '../api/logger';

const OPERATION_NAME = 'graphql_get_pricing_quote';
const MAX_ATTEMPTS = 5;
const RETRY_DELAY = 1000;

// Example set of params
/*
{
    "createTravelerCheckoutRequest": {
        "adults": 3,
        "arrivalDate": "2019-01-04",
        "departureDate": "2019-01-09",
        "unit": "/units/0004/700c502b-b30d-4b70-a640-2e355cbc6fd5",
        "pets": 0,
        "currency": "USD",
        "listingRef": "321.887611.1435551"
    }
}
 */
/* istanbul ignore next */
const pricingQuery = `
    query priceDetails($createTravelerCheckoutRequest: CreateTravelerCheckoutRequest) {
        priceDetails(createTravelerCheckoutRequest: $createTravelerCheckoutRequest) {
            notes {
                description
            }
            totals {
                title
                amount
                tooltip
            }
            checkoutUrl
            instantBooking
            dueNow {
                title
                amount
                tooltips {
                    title
                    amount
                    tooltip
                }
            }
            payments {
                title
                amount
                paidText
                infoText
                status
                viewUrl
            }
            averageNightlies {
                perNightCost {
                    currency
                    amount
                    localized
                }
                type
            }
            edapEventJson
            edapQuoteSuccess
            lineItems {
                title
                amount
                type
                tooltip
                subItems {
                    title
                    amount
                    type
                    tooltip
                }
            }
        }
    }
`;

/* eslint-disable complexity */
async function pricingGraphql({unit, arrivalDate, departureDate, adults, children = 0, pets = 0, listingRef = '', currency = 'USD'}, locale = '') {
    const pricingOptions = {
        unit,
        arrivalDate,
        departureDate,
        children,
        adults,
        pets,
        listingRef,
        currency
    };

    let attempt = 0;
    let result = null;
    do {
        attempt++;

        result = await graphqlQuery(OPERATION_NAME, pricingQuery, {createTravelerCheckoutRequest: pricingOptions}, locale, true);

        // check the error code on the response and if it's a "429 - Too Many Requests", we sleep for a bit and try again
        // NOTE: this logic allows us to use a low RPS limit for the pricing endpoint while not preventing travelers from getting prices
        if (result.errorCode === 429) {
            await new Promise((resolve) => {
                setTimeout(resolve, RETRY_DELAY)
            });

            continue;
        }

        break;
    } while (attempt < MAX_ATTEMPTS);

    // log a warning if we had to retry at least once
    if (attempt > 1) {
        log({
            message: `Attempted GQL pricing operation times="${attempt}" finalErrorCode="${result.errorCode || ''}"`
        });
    }

    return result;
}

/* eslint-enable complexity */

export default pricingGraphql;
export {pricingGraphql};
